import i18next from 'i18next';

/**
 * Formats a storage location's display label to include its storage type,
 * e.g. "Freezer A (Freezer)". Falls back to the plain name when no storage
 * type is present, keeping the label backward compatible.
 *
 * The returned string is for display only — callers must continue to use
 * the location's id/name as the underlying selected value.
 */
export function formatLocationLabel(name: string, storageType?: string | null): string {
  return storageType ? `${name} (${storageType})` : name;
}

/** Maps raw milk storage type enum values to their i18n label keys. */
const MILK_STORAGE_TYPE_KEYS: Record<string, string> = {
  REFRIGERATOR: 'settings.milkFormulaStorage.milkStorageTypeRefrigerator',
  FREEZER: 'settings.expiryRules.locationFreezer',
  DEEP_FREEZER: 'settings.expiryRules.locationDeepFreezer',
};

/**
 * Converts a raw enum-style value (e.g. "SHELF", "DEEP_FREEZER") into a
 * title-case display string (e.g. "Shelf", "Deep Freezer"). Used as a
 * fallback for storage types that don't have a dedicated i18n translation.
 */
export function toTitleCase(value: string): string {
  return value
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Translates a raw milk storage type enum value (e.g. "DEEP_FREEZER") into its
 * sentence-case display label (e.g. "Deep Freezer"). Falls back to a
 * title-cased version of the raw value when it is unrecognized or
 * untranslated, so callers stay backward compatible with any storage type
 * the backend may return while still avoiding raw uppercase enum values in
 * the UI.
 *
 * Uses the raw `i18next` instance directly (rather than the `useEnumTranslation`
 * Vue composable) so it can be called from non-component code such as Pinia
 * stores and API service modules.
 */
export function getMilkStorageTypeLabel(storageType?: string | null): string | undefined {
  if (!storageType) return storageType ?? undefined;
  const key = MILK_STORAGE_TYPE_KEYS[storageType];
  if (!key) return toTitleCase(storageType);
  const translated = i18next.t(key);
  return translated === key ? toTitleCase(storageType) : translated;
}
